import type { MonitoringArea } from '../../../types/auth';
import { formatMonitoringAreaLabel } from '../../../services/lguMonitoringArea';

function normalizePsaCode(code: unknown): string {
  let raw = String(code ?? '').trim().toUpperCase();
  if (!raw) return '';
  if (raw.startsWith('PH')) raw = raw.slice(2);
  return raw.replace(/\D/g, '');
}

function stripGadmRegionZero(digits: string): string {
  if (digits.length >= 4 && digits[2] === '0' && /^\d{2}/.test(digits)) {
    return digits.slice(0, 2) + digits.slice(3);
  }
  return digits;
}

function codeCore(digits: string): string {
  return digits.replace(/^0+/, '') || '0';
}

function namesMatch(expected: unknown, actual: unknown): boolean {
  const left = String(expected ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
  const right = String(actual ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (!left || !right) return false;
  return left === right || left.includes(right) || right.includes(left);
}

function psaCodesMatch(
  expected: unknown,
  actual: unknown,
  level: 'province' | 'municipality' | 'barangay'
): boolean {
  const exp = normalizePsaCode(expected);
  const act = normalizePsaCode(actual);
  if (!exp || !act) return false;
  if (exp === act) return true;
  const expN = stripGadmRegionZero(exp);
  const actN = stripGadmRegionZero(act);
  if (expN === actN || codeCore(expN) === codeCore(actN)) return true;
  if (level === 'province') {
    const prefix = expN.length >= 4 ? expN.slice(0, 4) : expN;
    const actPrefix = actN.length >= 4 ? actN.slice(0, 4) : actN;
    return actN.startsWith(prefix) || prefix.startsWith(actPrefix) || codeCore(prefix) === codeCore(actPrefix);
  }
  if (level === 'municipality') {
    const prefix = expN.length >= 6 ? expN.slice(0, 6) : expN;
    const actPrefix = actN.length >= 6 ? actN.slice(0, 6) : actN;
    return actN.startsWith(prefix) || prefix.startsWith(actPrefix) || codeCore(prefix) === codeCore(actPrefix);
  }
  return expN.includes(actN) || actN.includes(expN);
}

export type LocationCodesForArea = {
  province?: string | null;
  municipality?: string | null;
  barangay?: string | null;
  provinceName?: string | null;
  municipalityName?: string | null;
  barangayName?: string | null;
};

export function locationMatchesMonitoringArea(
  codes: LocationCodesForArea | null | undefined,
  area: MonitoringArea | null | undefined
): { ok: boolean; reason?: string; areaLabel?: string | null } {
  const areaLabel = formatMonitoringAreaLabel(area);
  if (!area) {
    return {
      ok: false,
      reason: 'no_monitoring_area',
      areaLabel,
    };
  }
  const level = String(area.level || '').trim().toLowerCase();
  const province = codes?.province ?? '';
  const municipality = codes?.municipality ?? '';
  const barangay = codes?.barangay ?? '';

  if (level === 'province') {
    const ok =
      psaCodesMatch(area.province_id, province, 'province') ||
      psaCodesMatch(area.province_code, province, 'province') ||
      namesMatch(area.province, province) ||
      namesMatch(area.province, codes?.provinceName);
    return {
      ok,
      reason: ok ? undefined : 'outside_monitoring_province',
      areaLabel,
    };
  }

  if (level === 'municipality') {
    const ok =
      psaCodesMatch(area.city_id, municipality, 'municipality') ||
      psaCodesMatch(area.city_code, municipality, 'municipality') ||
      namesMatch(area.municipality, municipality) ||
      namesMatch(area.municipality, codes?.municipalityName);
    return {
      ok,
      reason: ok ? undefined : 'outside_monitoring_municipality',
      areaLabel,
    };
  }

  if (level === 'barangay') {
    const cityOk = area.city_id || area.city_code
      ? psaCodesMatch(area.city_id, municipality, 'municipality') ||
        psaCodesMatch(area.city_code, municipality, 'municipality') ||
        namesMatch(area.municipality, municipality) ||
        namesMatch(area.municipality, codes?.municipalityName)
      : true;
    const brgyOk =
      psaCodesMatch(area.barangay_id, barangay, 'barangay') ||
      psaCodesMatch(area.barangay_code, barangay, 'barangay') ||
      namesMatch(area.barangay, barangay) ||
      namesMatch(area.barangay, codes?.barangayName);
    const ok = cityOk && brgyOk;
    return {
      ok,
      reason: ok
        ? undefined
        : !cityOk
          ? 'outside_monitoring_municipality'
          : 'outside_monitoring_barangay',
      areaLabel,
    };
  }

  return {
    ok: false,
    reason: 'unsupported_monitoring_level',
    areaLabel,
  };
}

export function monitoringAreaSaveError(
  match: { ok: boolean; reason?: string; areaLabel?: string | null }
): string | null {
  if (match.ok) return null;
  if (match.reason === 'no_monitoring_area') {
    return 'This LGU account has no monitoring area assigned. Ask an admin to assign one before saving fields.';
  }
  const where = match.areaLabel ? ` (${match.areaLabel})` : '';
  return `This field is outside your monitoring area${where}. Draw or detect fields inside that area only.`;
}
